import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { BacktestRunDetail } from './BacktestRunDetail';

// Pull ?key=<token> out of the hash (`#/backtest-run?key=…`). Re-read on hashchange
// so pasting a new link without a full reload still resolves.
function readKey(): string | null {
  const raw = window.location.hash || '';
  const q = raw.split('?')[1];
  if (!q) return null;
  return new URLSearchParams(q).get('key');
}

/**
 * Public, unauthenticated page for an expiring backtest-run share link.
 *
 * Rendered for `#/backtest-run?key=<token>` OUTSIDE the dashboard auth gate
 * (see App.tsx) so a recipient without an account can open it. BacktestRunDetail
 * resolves the token via the anonymous-safe `by-share` endpoint and renders the
 * full stats + equity + trades read-only (per-trade charts need auth, so they're
 * disabled for anonymous viewers).
 */
export const SharedBacktestRunPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => readKey());

  useEffect(() => {
    const onHash = () => setToken(readKey());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Lightweight public header — no nav, just the brand. */}
      <header className="border-b">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-white" />
          </div>
          <div className="text-sm font-bold tracking-tight">
            <span>Trade</span><span className="text-primary">Execute</span>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6">
        {token
          ? <BacktestRunDetail shareToken={token} />
          : <div className="text-sm text-red-500">This share link is missing its token.</div>}
      </main>
    </div>
  );
};

export default SharedBacktestRunPage;
